import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Notification, NotificationWithRelations } from '../types/notifications';
import { useAuth } from './useAuth';

export const useAdminNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user) {
      console.log('User autenticado, buscando notificações:', user.id);
      fetchNotifications();
      const unsubscribe = subscribeToNotifications();
      return () => {
        unsubscribe();
      };
    } else {
      console.log('Nenhum usuário autenticado');
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user]);

  useEffect(() => {
    // Atualizar unreadCount sempre que as notificações mudarem
    const count = notifications.filter(n => !n.read).length;
    console.log('Atualizando unreadCount:', count);
    setUnreadCount(count);
  }, [notifications]);

  const fetchNotifications = async () => {
    if (!user) {
      console.log('Tentativa de buscar notificações sem usuário');
      return;
    }

    try {
      setLoading(true);
      console.log('Buscando notificações para o usuário:', user.id);
      
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (filter === 'unread') {
        query = query.eq('read', false);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro na query do Supabase:', error);
        throw error;
      }

      console.log('Notificações recebidas:', data);
      const typedData = data as NotificationWithRelations[];
      setNotifications(typedData);
      setUnreadCount(typedData.filter(n => !n.read).length);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToNotifications = () => {
    if (!user) {
      console.log('Tentativa de subscrever sem usuário');
      return () => {};
    }

    console.log('Configurando subscription para notificações');
    const subscription = supabase
      .channel('admin-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          console.log('Mudança nas notificações recebida:', payload);
          
          // Atualizar imediatamente o estado local antes de buscar todas as notificações
          if (payload.eventType === 'INSERT') {
            const newNotification = payload.new as NotificationWithRelations;
            setNotifications(prev => [newNotification, ...prev]);
            setUnreadCount(prev => prev + 1);
          } else if (payload.eventType === 'DELETE') {
            const deletedId = payload.old.id;
            setNotifications(prev => {
              const wasUnread = prev.find(n => n.id === deletedId)?.read === false;
              if (wasUnread) {
                setUnreadCount(count => Math.max(0, count - 1));
              }
              return prev.filter(n => n.id !== deletedId);
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedNotification = payload.new as NotificationWithRelations;
            setNotifications(prev => {
              const wasUnread = prev.find(n => n.id === updatedNotification.id)?.read === false;
              const isNowRead = updatedNotification.read;
              if (wasUnread && isNowRead) {
                setUnreadCount(count => Math.max(0, count - 1));
              }
              return prev.map(n => n.id === updatedNotification.id ? updatedNotification : n);
            });
          }

          // Buscar todas as notificações para garantir sincronização
          await fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      console.log('Cancelando subscription');
      subscription.unsubscribe();
    };
  };

  const createNotification = async (notification: Partial<Notification> & { user_id: string }) => {
    try {
      console.log('Iniciando criação de notificação:', { 
        notification
      });

      // Validar dados antes de inserir
      if (!notification.title || !notification.message || !notification.type || !notification.user_id) {
        console.error('Dados de notificação inválidos:', notification);
        throw new Error('Dados de notificação inválidos');
      }

      const notificationData = {
        ...notification,
        read: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('Dados formatados para inserção:', notificationData);

      const { data, error } = await supabase
        .from('notifications')
        .insert([notificationData])
        .select('*');

      if (error) {
        console.error('Erro do Supabase ao inserir:', error);
        console.error('Detalhes do erro:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      console.log('Notificação criada com sucesso:', data);

      if (data && data[0]) {
        // Atualizar o estado local imediatamente
        const newNotification = data[0] as NotificationWithRelations;
        
        setNotifications(prev => {
          const updated = [newNotification, ...prev];
          console.log('Estado de notificações atualizado:', updated);
          return updated;
        });

        // Atualizar o contador de não lidas
        setUnreadCount(prev => prev + 1);
        
        return newNotification;
      }

      return null;
    } catch (error) {
      console.error('Erro ao criar notificação:', error);
      return null;
    }
  };

  const markAsRead = async (notificationId: string) => {
    if (!user) {
      console.error('Tentativa de marcar como lida sem usuário');
      return;
    }

    try {
      console.log('Marcando notificação como lida:', notificationId);
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, updated_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) {
      console.error('Tentativa de marcar todas como lidas sem usuário');
      return;
    }

    try {
      console.log('Marcando todas as notificações como lidas');
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, updated_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    if (!user) {
      console.error('Tentativa de deletar notificação sem usuário');
      return;
    }

    try {
      console.log('Deletando notificação:', notificationId);
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (error) throw error;

      setNotifications(prev =>
        prev.filter(n => n.id !== notificationId)
      );
    } catch (error) {
      console.error('Erro ao apagar notificação:', error);
    }
  };

  const deleteAllNotifications = async () => {
    if (!user) {
      console.error('Tentativa de deletar todas as notificações sem usuário');
      return;
    }

    try {
      console.log('Deletando todas as notificações');
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      setNotifications([]);
    } catch (error) {
      console.error('Erro ao apagar todas as notificações:', error);
    }
  };

  return {
    notifications,
    loading,
    filter,
    setFilter,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    createNotification,
    fetchNotifications,
    unreadCount
  };
}; 